import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Divider,
  Chip,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp,
  Notifications,
  Settings,
  Analytics,
  Save,
  Refresh,
  LocationSearching,
  Message
} from '@mui/icons-material';
import { API_URL } from './config';

const RealtimeSettings = () => {
  const [settings, setSettings] = useState({
    // Commission settings
    percentage_commission_rate: 20,
    daily_flat_figure_commission: 5,
    daily_promotion_active: false,
    promotion_start_date: null,
    promotion_end_date: null,
    lock_to_percentage: false,
    auto_switch_percentage: true,
    // Ride-matching detour distance settings
    default_detour_distance: 20.0,
    min_detour_distance: 0.5,
    max_detour_distance: 30.0,
    detour_increment: 0.5,
    auto_detour_expansion: false,
    expansion_time_limit: 300,
    max_auto_expansions: 3,
    // Nearby requests settings
    nearby_requests_radius_km: 5.0,
    // NEW: Rideshare settings
    rideshare_request_expiry_minutes: 10,
    rideshare_max_active_requests: 1,
    private_ride_sms_radius_km: 5.0,
    private_ride_sms_closest_drivers: 3,
    rideshare_request_notify_radius_km: 5.0,
    rideshare_request_notify_closest_drivers: 5,
    regular_delivery_sms_radius_km: 5.0,
    regular_delivery_sms_closest_drivers: 5,
    // Editable on this page since 2026-10-06. Kept in the notification-settings payload below.
    sms_min_drivers_threshold: 10,
    // 'always' texts the nearest drivers for every new request; 'low_availability' only when few are nearby.
    sms_mode: 'always'
  });
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    commission: true,
    promotion: false,
    ridematching: false,
    newTripSmsNotification: false
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [promotionStatus, setPromotionStatus] = useState('inactive');
  const [calculatingPerformance, setCalculatingPerformance] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    fetchCommissionSettings();
  }, []);

  const fetchCommissionSettings = async () => {
    try {
      setLoading(true);
      
      const [realtimeResponse, notificationSettingsResponse] = await Promise.all([
        fetch(`${API_URL}/realtime-settings/settings`),
        fetch(`${API_URL}/control-panel/settings/notification-settings`)
      ]);

      const realtimeData = await realtimeResponse.json();
      const notificationSettingsData = await notificationSettingsResponse.json().catch(() => null);

      if (realtimeData.status === 200) {
        const updatedSettings = {
          // Commission settings
          percentage_commission_rate: realtimeData.settings.commission?.percentage_commission_rate || 20,
          daily_flat_figure_commission: realtimeData.settings.commission?.daily_flat_figure_commission || 5,
          daily_promotion_active: realtimeData.settings.commission?.promotion_active || false,
          promotion_start_date: realtimeData.settings.commission?.promotion_start_date || null,
          promotion_end_date: realtimeData.settings.commission?.promotion_end_date || null,
          lock_to_percentage: realtimeData.settings.commission?.lock_to_percentage || false,
          auto_switch_percentage: realtimeData.settings.commission?.auto_switch_percentage || true,
          // Rideshare settings (NEW)
          rideshare_request_expiry_minutes: realtimeData.settings.rideshare?.request_expiry_minutes || 10,
          rideshare_max_active_requests: realtimeData.settings.rideshare?.max_active_requests || 1,
          nearby_requests_radius_km: realtimeData.settings.rideshare?.detour_limit_km || 5.0,
          // Keep existing detour settings for backward compatibility
          default_detour_distance: realtimeData.settings.rideshare?.detour_limit_km || 20.0,
          min_detour_distance: 0.5,
          max_detour_distance: 30.0,
          detour_increment: 0.5,
          auto_detour_expansion: false,
          expansion_time_limit: 300,
          max_auto_expansions: 3
        };

        const notificationSettings = notificationSettingsData?.success ? notificationSettingsData.data : null;
        if (notificationSettings) {
          updatedSettings.private_ride_sms_radius_km = notificationSettings.private_ride_sms?.radius_km ?? 5.0;
          updatedSettings.private_ride_sms_closest_drivers = notificationSettings.private_ride_sms?.closest_drivers ?? 3;
          updatedSettings.rideshare_request_notify_radius_km = notificationSettings.rideshare_request_notify?.radius_km ?? 5.0;
          updatedSettings.rideshare_request_notify_closest_drivers = notificationSettings.rideshare_request_notify?.closest_drivers ?? 5;
          updatedSettings.regular_delivery_sms_radius_km = notificationSettings.regular_delivery_sms?.radius_km ?? 5.0;
          updatedSettings.regular_delivery_sms_closest_drivers = notificationSettings.regular_delivery_sms?.closest_drivers ?? 5;
          updatedSettings.sms_min_drivers_threshold = notificationSettings.sms_min_drivers_threshold ?? 10;
          updatedSettings.sms_mode = notificationSettings.sms_mode ?? 'always';
        }
        
        setSettings(updatedSettings);
        updatePromotionStatus(updatedSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Failed to fetch settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePromotionStatus = (currentSettings) => {
    if (!currentSettings.daily_promotion_active) {
      setPromotionStatus('inactive');
    } else {
      const now = new Date();
      const start = currentSettings.promotion_start_date ? new Date(currentSettings.promotion_start_date) : null;
      const end = currentSettings.promotion_end_date ? new Date(currentSettings.promotion_end_date) : null;
      
      if (!start || !end) {
        setPromotionStatus('invalid');
      } else if (now < start) {
        setPromotionStatus('scheduled');
      } else if (now > end) {
        setPromotionStatus('expired');
      } else {
        setPromotionStatus('active');
      }
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateSettings = () => {
    if (!settings.percentage_commission_rate || settings.percentage_commission_rate <= 0) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Percentage commission rate must be greater than 0'
      });
      return false;
    }

    if (!settings.daily_flat_figure_commission || settings.daily_flat_figure_commission < 0) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Daily flat figure commission must be non-negative'
      });
      return false;
    }

    if (settings.daily_promotion_active) {
      if (!settings.promotion_start_date || !settings.promotion_end_date) {
        setAlert({
          show: true,
          type: 'error',
          message: 'Promotion start and end dates are required when promotion is active'
        });
        return false;
      }

      if (new Date(settings.promotion_end_date) <= new Date(settings.promotion_start_date)) {
        setAlert({
          show: true,
          type: 'error',
          message: 'Promotion end date must be after start date'
        });
        return false;
      }
    }

    return true;
  };

  const handleSaveSettings = async () => {
    if (!validateSettings()) return;

    try {
      setSaving(true);
      
      // Extract commission settings
      const commissionSettings = {
        percentage_commission_rate: settings.percentage_commission_rate,
        daily_flat_figure_commission: settings.daily_flat_figure_commission,
        daily_promotion_active: settings.daily_promotion_active,
        promotion_start_date: settings.promotion_start_date,
        promotion_end_date: settings.promotion_end_date,
        lock_to_percentage: settings.lock_to_percentage,
        auto_switch_percentage: settings.auto_switch_percentage
      };
      
      // Extract rideshare settings (NEW)
      const rideshareSettings = {
        request_expiry_minutes: settings.rideshare_request_expiry_minutes,
        detour_limit_km: settings.nearby_requests_radius_km, // This updates nearby_requests_radius_km
        max_active_requests: settings.rideshare_max_active_requests
      };
      
      // Save commission settings
      const commissionResponse = await fetch(`${API_URL}/commission/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commissionSettings),
      });

      // Save rideshare settings using the new API
      const rideshareResponse = await fetch(`${API_URL}/realtime-settings/rideshare`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rideshareSettings),
      });

      const notificationSettingsPayload = {
        private_ride_sms: {
          radius_km: settings.private_ride_sms_radius_km,
          closest_drivers: settings.private_ride_sms_closest_drivers
        },
        rideshare_request_notify: {
          radius_km: settings.rideshare_request_notify_radius_km,
          closest_drivers: settings.rideshare_request_notify_closest_drivers
        },
        regular_delivery_sms: {
          radius_km: settings.regular_delivery_sms_radius_km,
          closest_drivers: settings.regular_delivery_sms_closest_drivers
        },
        // Editable on this page since 2026-10-06.
        sms_min_drivers_threshold: settings.sms_min_drivers_threshold,
        sms_mode: settings.sms_mode
      };

      const notificationSettingsResponse = await fetch(`${API_URL}/control-panel/settings/notification-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notificationSettingsPayload),
      });

      const commissionData = await commissionResponse.json();
      const rideshareData = await rideshareResponse.json();
      const notificationSettingsResult = await notificationSettingsResponse.json().catch(() => null);
      
      if (commissionData.status === 200 && rideshareData.status === 200 && notificationSettingsResult?.success) {
        setAlert({
          show: true,
          type: 'success',
          message: 'All settings updated successfully'
        });
        updatePromotionStatus(settings);
      } else {
        throw new Error('Failed to update some settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Failed to update settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const getPromotionStatusColor = () => {
    switch (promotionStatus) {
      case 'active': return 'success';
      case 'scheduled': return 'info';
      case 'expired': return 'warning';
      case 'invalid': return 'error';
      default: return 'default';
    }
  };

  const getPromotionStatusText = () => {
    switch (promotionStatus) {
      case 'active': return 'Active';
      case 'scheduled': return 'Scheduled';
      case 'expired': return 'Expired';
      case 'invalid': return 'Invalid';
      default: return 'Inactive';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Loading commission settings...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        maxWidth: 1200, 
        mx: 'auto', 
        p: { xs: 2, sm: 3 },
        bgcolor: '#f8f9fa',
        minHeight: '100vh'
      }}>
        {/* Header */}
        <Box sx={{ 
          mb: 4, 
          p: 3, 
          bgcolor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Settings sx={{ mr: 2, fontSize: 32, color: '#1976d2' }} />
              <Box>
                <Typography variant="h4" fontWeight="600" color="#333" sx={{ mb: 0.5 }}>
                  Realtime Settings
                </Typography>
                <Typography variant="body2" color="#666">
                  Configure commission rates, promotions, and ride-matching settings in real-time
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchCommissionSettings}
              disabled={loading}
              sx={{ borderColor: '#1976d2', color: '#1976d2' }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 3 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Analytics Section - Collapsible */}
        <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#eeeeee' }
            }}
            onClick={() => toggleSection('analytics')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Analytics sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                Commission Analytics Overview
              </Typography>
            </Box>
            <Typography sx={{ color: '#666' }}>
              {expandedSections.analytics ? '▼' : '▶'}
            </Typography>
          </Box>
          
          {expandedSections.analytics && (
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ textAlign: 'center', p: 3, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                    <Typography variant="h3" color="#1976d2" fontWeight="700">
                      {settings.percentage_commission_rate || 0}%
                    </Typography>
                    <Typography variant="body2" color="#666" sx={{ mt: 1 }}>
                      Current Percentage Rate
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ textAlign: 'center', p: 3, bgcolor: '#e8f5e8', border: '1px solid #a5d6a7' }}>
                    <Typography variant="h3" color="#2e7d32" fontWeight="700">
                      ${settings.daily_flat_figure_commission || 0}
                    </Typography>
                    <Typography variant="body2" color="#666" sx={{ mt: 1 }}>
                      Daily Flat Commission
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ textAlign: 'center', p: 3, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                    <Typography variant="h3" color="#f57c00" fontWeight="700">
                      {getPromotionStatusText()}
                    </Typography>
                    <Typography variant="body2" color="#666" sx={{ mt: 1 }}>
                      Promotion Status
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 3, 
                    bgcolor: settings.lock_to_percentage ? '#ffebee' : '#e8f5e8',
                    border: settings.lock_to_percentage ? '1px solid #ef9a9a' : '1px solid #a5d6a7'
                  }}>
                    <Typography variant="h3" fontWeight="700" color={settings.lock_to_percentage ? '#c62828' : '#2e7d32'}>
                      {settings.lock_to_percentage ? 'Locked' : 'Flexible'}
                    </Typography>
                    <Typography variant="body2" color="#666" sx={{ mt: 1 }}>
                      Commission Mode
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          )}
        </Card>

        {/* Commission Rates Section - Collapsible */}
        <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#eeeeee' }
            }}
            onClick={() => toggleSection('commission')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                Commission Rates
              </Typography>
            </Box>
            <Typography sx={{ color: '#666' }}>
              {expandedSections.commission ? '▼' : '▶'}
            </Typography>
          </Box>
          
          {expandedSections.commission && (
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Percentage Commission Rate (%)"
                  type="number"
                  value={settings.percentage_commission_rate || 20}
                  onChange={(e) => handleSettingChange('percentage_commission_rate', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText="Commission percentage charged per trip"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Daily Flat Figure Commission ($)"
                  type="number"
                  value={settings.daily_flat_figure_commission || 5}
                  onChange={(e) => handleSettingChange('daily_flat_figure_commission', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                  helperText="Fixed daily commission charged once per day"
                />
              </Box>

              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="600" sx={{ mb: 1 }}>
                  💡 Example Calculations:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Percentage: $100 fare × {settings.percentage_commission_rate}% = ${(100 * settings.percentage_commission_rate / 100).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Daily: ${settings.daily_flat_figure_commission} charged once per day
                </Typography>
              </Box>
            </CardContent>
          )}
        </Card>

          {/* Promotion Settings Section - Collapsible */}
        <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#eeeeee' }
            }}
            onClick={() => toggleSection('promotion')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Notifications sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                Promotion Settings
              </Typography>
            </Box>
            <Typography sx={{ color: '#666' }}>
              {expandedSections.promotion ? '▼' : '▶'}
            </Typography>
          </Box>
          
          {expandedSections.promotion && (
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.daily_promotion_active}
                      onChange={(e) => handleSettingChange('daily_promotion_active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable Daily Commission Promotion"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  Allow users to switch to daily flat rate commission
                </Typography>
              </Box>

              {settings.daily_promotion_active && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <DateTimePicker
                      label="Promotion Start Date & Time"
                      value={settings.promotion_start_date || null}
                      onChange={(value) => handleSettingChange('promotion_start_date', value)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <DateTimePicker
                      label="Promotion End Date & Time"
                      value={settings.promotion_end_date || null}
                      onChange={(value) => handleSettingChange('promotion_end_date', value)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Box>
                </>
              )}

              <Box sx={{ mb: 3 }}>
                <Chip
                  label={`Promotion Status: ${getPromotionStatusText()}`}
                  color={getPromotionStatusColor()}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Current promotion status based on dates and settings
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.lock_to_percentage}
                      onChange={(e) => handleSettingChange('lock_to_percentage', e.target.checked)}
                      color="warning"
                    />
                  }
                  label="Lock Users to Percentage Commission"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  Force all users to use percentage commission only
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.auto_switch_percentage}
                      onChange={(e) => handleSettingChange('auto_switch_percentage', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Auto-switch to Percentage When Promotion Ends"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  Automatically switch users back to percentage when promotion expires
                </Typography>
              </Box>
            </CardContent>
          )}
        </Card>

          {/* Ride-Matching Settings Section - Collapsible */}
        <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#eeeeee' }
            }}
            onClick={() => toggleSection('ridematching')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationSearching sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                Ride-Matching Settings
              </Typography>
            </Box>
            <Typography sx={{ color: '#666' }}>
              {expandedSections.ridematching ? '▼' : '▶'}
            </Typography>
          </Box>
          
          {expandedSections.ridematching && (
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Default Detour Distance (km)"
                  type="number"
                  value={settings.default_detour_distance || 3}
                  onChange={(e) => handleSettingChange('default_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: settings.min_detour_distance, max: settings.max_detour_distance, step: settings.detour_increment }}
                  helperText={`Maximum detour distance for passenger matching (${settings.min_detour_distance}km - ${settings.max_detour_distance}km)`}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Minimum Detour Distance (km)"
                  type="number"
                  value={settings.min_detour_distance || 0.5}
                  onChange={(e) => handleSettingChange('min_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0.1, max: 2, step: 0.1 }}
                  helperText="Minimum allowed detour distance for passengers"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Maximum Detour Distance (km)"
                  type="number"
                  value={settings.max_detour_distance || 10}
                  onChange={(e) => handleSettingChange('max_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 5, max: 50, step: 1 }}
                  helperText="Maximum allowed detour distance for passengers"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Detour Increment (km)"
                  type="number"
                  value={settings.detour_increment || 0.5}
                  onChange={(e) => handleSettingChange('detour_increment', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0.1, max: 2, step: 0.1 }}
                  helperText="Step size for detour distance adjustments"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.auto_detour_expansion}
                      onChange={(e) => handleSettingChange('auto_detour_expansion', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Auto-Expand Detour Distance if No Matches"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  Automatically increase detour distance if no passengers found within time limit
                </Typography>
              </Box>

              {settings.auto_detour_expansion && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Expansion Time Limit (seconds)"
                      type="number"
                      value={settings.expansion_time_limit || 300}
                      onChange={(e) => handleSettingChange('expansion_time_limit', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 60, max: 1800, step: 30 }}
                      helperText="Time to wait before expanding detour distance (1-30 minutes)"
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Max Auto-Expansions"
                      type="number"
                      value={settings.max_auto_expansions || 3}
                      onChange={(e) => handleSettingChange('max_auto_expansions', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 1, max: 10, step: 1 }}
                      helperText="Maximum number of automatic detour distance expansions"
                    />
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                📍 Nearby Requests Settings
              </Typography>

              <Box sx={{ 
                mb: 3, 
                p: 2, 
                border: '2px dashed #1976d2', 
                borderRadius: 2, 
                backgroundColor: '#f3f8ff' 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    📍 Current Radius: {settings.nearby_requests_radius_km || 5}km
                  </Typography>
                </Box>
                <TextField
                  label="Nearby Requests Radius (km)"
                  type="number"
                  value={settings.nearby_requests_radius_km || 5}
                  onChange={(e) => handleSettingChange('nearby_requests_radius_km', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1, max: 20, step: 0.5 }}
                  helperText="Radius for showing nearby passenger requests to drivers (helps optimize routes)"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    }
                  }}
                />
                
                {/* NEW: Rideshare Request Expiry Minutes */}
                <TextField
                  label="Rideshare Request Expiry (minutes)"
                  type="number"
                  value={settings.rideshare_request_expiry_minutes || 10}
                  onChange={(e) => handleSettingChange('rideshare_request_expiry_minutes', parseInt(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1, max: 60, step: 1 }}
                  helperText="Time before rideshare requests expire (1-60 minutes)"
                  sx={{ 
                    mt: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ff9800',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ff9800',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff9800',
                      },
                    }
                  }}
                />
                
                {/* NEW: Rideshare Max Active Requests */}
                <TextField
                  label="Max Active Requests per Passenger"
                  type="number"
                  value={settings.rideshare_max_active_requests || 1}
                  onChange={(e) => handleSettingChange('rideshare_max_active_requests', parseInt(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  helperText="Maximum number of active rideshare requests a passenger can have"
                  sx={{ 
                    mt: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#4caf50',
                      },
                      '&:hover fieldset': {
                        borderColor: '#4caf50',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4caf50',
                      },
                    }
                  }}
                />
              </Box>
            </CardContent>
          )}
        </Card>

        {/* New Trip SMS Notification Section - Collapsible */}
        <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#eeeeee' }
            }}
            onClick={() => toggleSection('newTripSmsNotification')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Message sx={{ mr: 2, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                New Trip SMS Notification
              </Typography>
            </Box>
            <Typography sx={{ color: '#666' }}>
              {expandedSections.newTripSmsNotification ? '▼' : '▶'}
            </Typography>
          </Box>
          
          {expandedSections.newTripSmsNotification && (
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                <Typography variant="body2" color="#1565c0" fontWeight="600" sx={{ mb: 1 }}>
                  📱 Configure SMS notifications for different trip types
                </Typography>
                <Typography variant="body2" color="#1976d2">
                  Set the radius and number of closest drivers to notify when new trips are created. This helps optimize driver matching and reduce response times.
                </Typography>
              </Box>

              {/* Private Ride SMS Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                  🚗 New Private Ride SMS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When a customer creates a new private ride request, notify the closest available drivers via SMS.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Notification Radius (km)"
                      type="number"
                      value={settings.private_ride_sms_radius_km}
                      onChange={(e) => handleSettingChange('private_ride_sms_radius_km', parseFloat(e.target.value))}
                      fullWidth
                      inputProps={{ min: 0.5, max: 50, step: 0.5 }}
                      helperText="Search radius for finding nearby drivers"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#2e7d32',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2e7d32',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2e7d32',
                          },
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Number of Closest Drivers to Notify"
                      type="number"
                      value={settings.private_ride_sms_closest_drivers}
                      onChange={(e) => handleSettingChange('private_ride_sms_closest_drivers', parseInt(e.target.value, 10))}
                      fullWidth
                      inputProps={{ min: 1, max: 50, step: 1 }}
                      helperText="Maximum drivers to send SMS notifications to"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#2e7d32',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2e7d32',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2e7d32',
                          },
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Rideshare Request SMS Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', display: 'flex', alignItems: 'center' }}>
                  🚌 Rideshare Passenger Request → Driver Notification
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When a passenger creates a rideshare request, notify nearby drivers who may be interested in picking up additional passengers.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Notification Radius (km)"
                      type="number"
                      value={settings.rideshare_request_notify_radius_km}
                      onChange={(e) => handleSettingChange('rideshare_request_notify_radius_km', parseFloat(e.target.value))}
                      fullWidth
                      inputProps={{ min: 0.5, max: 50, step: 0.5 }}
                      helperText="Search radius for finding drivers near the passenger route"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ff9800',
                          },
                          '&:hover fieldset': {
                            borderColor: '#ff9800',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ff9800',
                          },
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Number of Closest Drivers to Notify"
                      type="number"
                      value={settings.rideshare_request_notify_closest_drivers}
                      onChange={(e) => handleSettingChange('rideshare_request_notify_closest_drivers', parseInt(e.target.value, 10))}
                      fullWidth
                      inputProps={{ min: 1, max: 50, step: 1 }}
                      helperText="Maximum drivers to send rideshare notifications to"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ff9800',
                          },
                          '&:hover fieldset': {
                            borderColor: '#ff9800',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ff9800',
                          },
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Regular Delivery SMS Settings */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#9c27b0', display: 'flex', alignItems: 'center' }}>
                  📦 New Regular Delivery SMS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When a customer creates a new delivery request, notify the closest available drivers via SMS.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Notification Radius (km)"
                      type="number"
                      value={settings.regular_delivery_sms_radius_km}
                      onChange={(e) => handleSettingChange('regular_delivery_sms_radius_km', parseFloat(e.target.value))}
                      fullWidth
                      inputProps={{ min: 0.5, max: 50, step: 0.5 }}
                      helperText="Search radius for finding nearby delivery drivers"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#9c27b0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9c27b0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#9c27b0',
                          },
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Number of Closest Drivers to Notify"
                      type="number"
                      value={settings.regular_delivery_sms_closest_drivers}
                      onChange={(e) => handleSettingChange('regular_delivery_sms_closest_drivers', parseInt(e.target.value, 10))}
                      fullWidth
                      inputProps={{ min: 1, max: 50, step: 1 }}
                      helperText="Maximum drivers to send delivery notifications to"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#9c27b0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9c27b0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#9c27b0',
                          },
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="600" sx={{ mb: 1 }}>
                  💡 How SMS Notifications Work:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  • System finds drivers within the specified radius from the pickup location
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  • Selects the closest drivers based on current GPS location
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  • Sends an SMS with a link to the driver's requests list to the selected drivers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Drivers can accept or decline the trip request via their mobile app
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, mt: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="600" sx={{ mb: 1 }}>
                  📱 When to text drivers
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sms_mode !== 'low_availability'}
                      onChange={(e) => handleSettingChange('sms_mode', e.target.checked ? 'always' : 'low_availability')}
                    />
                  }
                  label="Text the nearest drivers for every new request"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {settings.sms_mode !== 'low_availability'
                    ? 'On: every new private ride, delivery and shared-ride request texts the nearest drivers (the number above), with a link to the requests list. Highest SMS cost.'
                    : 'Off: SMS is only a backup. Drivers are texted only when fewer than the number below are available nearby; otherwise the push notification alone is used.'}
                </Typography>
                {settings.sms_mode === 'low_availability' && (
                  <TextField
                    label="Minimum drivers nearby before SMS backup sends"
                    type="number"
                    value={settings.sms_min_drivers_threshold ?? 10}
                    onChange={(e) => handleSettingChange('sms_min_drivers_threshold', parseInt(e.target.value, 10))}
                    fullWidth
                    inputProps={{ min: 1, max: 100, step: 1 }}
                    helperText="SMS is only sent when fewer than this many drivers are found nearby. Higher = more SMS."
                  />
                )}
              </Box>
            </CardContent>
          )}
        </Card>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveSettings}
            disabled={saving}
            startIcon={<Save />}
            sx={{ 
              minWidth: 200,
              py: 1.5,
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }
            }}
          >
            {saving ? 'Saving Settings...' : 'Save All Settings'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default RealtimeSettings;
